import { FC, Fragment, useMemo } from "react";
import { Divider, ListSubheader, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LiveUsageResourceGridToolbarCleanActionItem } from "./LiveUsageResourceGridToolbarCleanActionItem";
import { ProviderAccessType, ResourceType } from "@/services/cloudchipr.api";
import { useAppSelector } from "@/store/hooks";
import { selectedResourcesByMasterIdSelector } from "@/store/resources-grid/selectors/grid/selection/selectedResourcesByMasterIdSelector";
import { selectedResourcesAccountAccessTypesSelector } from "@/store/resources-grid/selectors/grid/selection/account/selectedResourcesAccountAccessTypesSelector";
import { liveUsageResourceActionOptionsByVisibilitySelector } from "@/store/live-usage-mgmt/selectors/actions/liveUsageResourceActionOptionsByVisibilitySelector";
import { useAppAbility } from "@/services/permissions";
import { getUserPermissionInfoText } from "@/components/pages/utils/helpers/getUserPermissionInfoText";

interface LiveUsageResourceGridToolbarCleanActionsProps {
  onClose?(): void;
  masterResourceType: ResourceType;
  masterGridId: string;
}

export const LiveUsageResourceGridToolbarCleanActions: FC<
  LiveUsageResourceGridToolbarCleanActionsProps
> = ({ masterResourceType, masterGridId, onClose }) => {
  const { cannot } = useAppAbility();
  const canNotCleanResource = cannot("clean", "resource");

  const accessType = useAppSelector((state) =>
    selectedResourcesAccountAccessTypesSelector(state, masterGridId),
  );

  const selectedResources = useAppSelector((state) =>
    selectedResourcesByMasterIdSelector(state, masterGridId),
  );

  const isAnySelectedResourceProtected = useMemo(() => {
    return selectedResources?.some((resource) => resource?.is_protected);
  }, [selectedResources]);
  const areAllSelectedResourcesProtected = useMemo(() => {
    return (
      selectedResources?.filter((resource) => resource?.is_protected)
        ?.length === selectedResources?.length
    );
  }, [selectedResources]);

  const options = useAppSelector((state) =>
    liveUsageResourceActionOptionsByVisibilitySelector(
      state,
      masterGridId,
      masterResourceType,
    ),
  );

  if (!options?.length) {
    return null;
  }

  const showInfoIcon =
    canNotCleanResource ||
    accessType === "read" ||
    isAnySelectedResourceProtected;
  const infoText = getInfoText(
    canNotCleanResource,
    accessType,
    areAllSelectedResourcesProtected,
  );

  return (
    <Fragment>
      <ListSubheader sx={{ lineHeight: 2 }}>
        <Typography fontSize="smaller">
          ACTIONS
          {showInfoIcon && (
            <Tooltip arrow title={infoText}>
              <InfoOutlinedIcon
                sx={{ verticalAlign: "middle", color: "text.secondary", ml: 1 }}
                fontSize="small"
              />
            </Tooltip>
          )}
        </Typography>
      </ListSubheader>

      {options?.map((item) => (
        <LiveUsageResourceGridToolbarCleanActionItem
          item={item}
          key={item.value}
          onClose={onClose}
          masterGridId={masterGridId}
          masterResourceType={masterResourceType}
        />
      ))}

      <Divider />
    </Fragment>
  );
};

const getInfoText = (
  canNotCleanResource: boolean,
  accessType?: ProviderAccessType,
  areAllSelectedResourcesProtected?: boolean,
) => {
  if (canNotCleanResource) {
    return getUserPermissionInfoText("perform actions");
  }

  if (accessType === "read") {
    return "Resource actions can only be performed on Read/Write accounts. Please switch to Read/Write access mode to enable actions.";
  }

  if (areAllSelectedResourcesProtected) {
    return "Actions cannot be performed on protected resources.";
  }

  return "Actions cannot be performed on protected resources or resources in Read-Only accounts.";
};
